// Makes a refused browser-storage operation on an offline-recording preference
// VISIBLE, instead of losing it.
//
// The safe storage wrappers these preferences now use swallow a throwing
// `Storage` — quota exhaustion, Safari private mode, a locked-down webview —
// because a refused write must never break a render or abort the sign-out clear
// it sits inside. Swallowing is the right call there and stays. What was missing
// is the other half: the wrapper exposes a reporter seam and nothing in the atom
// layer passed one, so every refusal was invisible.
//
// Silence is defensible for a preference whose later step matters more than its
// persistence (a banner that stays undismissed, a panel that reopens). It is not
// defensible here. These two values are read back on the offline create path by
// non-reactive getters, on a cold offline boot — the one case where no `/user`
// response arrives to heal them. A refused write means the clinician's choice is
// simply gone on the next launch with nothing anywhere saying why.
//
// SCOPE, stated narrowly on purpose. This covers the wrapper's PER-OPERATION
// refusals — a `getItem`, `setItem` or `removeItem` that throws once the store
// object is in hand. It does NOT cover the case where accessing
// `window.localStorage` ITSELF throws, which a browser with site data blocked
// does: the shared wrapper catches that one outside the per-operation handlers
// and degrades to a no-op store, so no reporter is consulted and every later
// operation succeeds silently. Closing that gap means changing the shared
// wrapper's own body, which is a call for its owners rather than a change to
// make from a consumer — so the claim here is scoped to what this reporter can
// actually see.
//
// PHI CONTRACT. Presence and shape only: which atom, which storage, which
// operation, the frozen key name, and the error's CLASS. Never the stored value
// and never the error's message. The recorder snapshot carries a template id and
// a transcribe mode; warn-level logs forward to Datadog, so a payload that
// carried them would forward them too. The seam's own signature helps here — it
// hands the reporter the key and the error, never the value — but an error
// object can still quote what the browser was asked to write, which is why only
// `name` crosses.

import { type SafeStorageErrorHandler } from '@/lib/jotai-storage';
import { logger } from '@/utils/logger/logger';

/**
 * Which browser store the refused operation was aimed at. Passed in by the
 * declaring module rather than sniffed, so it stays right if a preference ever
 * moves stores — the wrapper does not tell its reporter which one it wrapped.
 */
export type OfflineStorageName = 'localStorage' | 'sessionStorage';

/**
 * The one message every offline-recording storage refusal logs under. Exported
 * so a suite asserts the real string rather than a copy that can drift out of
 * step with it.
 */
export const OFFLINE_STORAGE_FAILURE_MESSAGE =
  '[offlineRecording] browser storage refused a persisted preference';

/**
 * The error's class, never its message.
 *
 * A refused write is a `DOMException` in practice, whose `name`
 * (`QuotaExceededError`, `SecurityError`) is the whole diagnosis and carries
 * nothing user-derived. A message can quote the value the browser was asked to
 * store, so it does not cross. A non-`Error` rejection reports its primitive
 * type for the same reason — `String(error)` would be the value itself.
 */
const classify = (error: unknown): string =>
  error instanceof Error ? error.name : typeof error;

/**
 * Build the reporter for one preference module.
 *
 * `atomName` and `storage` are compile-time constants at every call site, which
 * is deliberate: the payload has to be safe by construction, not by a caller
 * remembering to redact.
 *
 * The returned handler is called from inside the wrapper's catch, which already
 * swallowed a storage throw and guards this call in turn — so it does no work
 * beyond formatting and one log line.
 */
export const createOfflineStorageFailureReporter = (
  atomName: string,
  storage: OfflineStorageName
): SafeStorageErrorHandler =>
  function reportOfflineStorageFailure(operation, key, error) {
    logger.warn(OFFLINE_STORAGE_FAILURE_MESSAGE, {
      atom: atomName,
      storage,
      operation,
      key,
      errorName: classify(error),
    });
  };
